/** Stub-pagina's voor modules die volledige UI in v3B.1 krijgen. */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { adminGet } from "../../lib/adminApi";
import { useUiPreference } from "../../lib/uiPreferences";
import { PageHeader, LoadingRow, EmptyRow, ErrorBanner, OvergeslagenBanner, StatusBadge, formatDate, formatDateOnly } from "../../components/admin/AdminUI";
import ViewModeToggle from "../../components/admin/ViewModeToggle";

function StubList({ title, subtitle, queryKey, endpoint, mapper, testid }) {
  const q = useQuery({ queryKey: [queryKey], queryFn: () => adminGet(endpoint, { limit: 50 }) });
  const items = q.data?.items || q.data || [];
  return (
    <div data-testid={testid}>
      <PageHeader title={title} subtitle={subtitle} />
      <ErrorBanner error={q.error} />
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 fulkra-tekst">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">{mapper.head}</thead>
          <tbody className="divide-y divide-slate-100">
            {q.isLoading ? <LoadingRow cols={mapper.cols} /> : items.length === 0 ? <EmptyRow cols={mapper.cols} /> : items.map(mapper.row)}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-500">Volledige UI met filters/CRUD volgt in v3B.1.</p>
    </div>
  );
}

export function AanmeetdagenPage() {
  return <AanmeetdagenTegels />;
}

function AanmeetdagenTegels() {
  const [werkgeverId, setWerkgeverId] = useState("");
  const [datumVan, setDatumVan] = useState("");
  const [datumTot, setDatumTot] = useState("");
  const [view, setView] = useUiPreference("aanmeetdagen_view", "blokjes");

  const werkgeversQ = useQuery({
    queryKey: ["werkgevers", "for-aanmeetdagen"],
    // 100 is het maximum dat de API toestaat (Query(le=100)). Met 200 gaf elke
    // aanroep een 422 en bleef de keuzelijst leeg — filteren op werkgever werkte
    // hier niet, zonder zichtbare fout.
    queryFn: () => adminGet("/api/admin/werkgevers", { limit: 100 }),
  });

  const params = { limit: 100 };
  if (werkgeverId) params.werkgever_id = werkgeverId;
  if (datumVan) params.datum_van = datumVan;
  if (datumTot) params.datum_tot = datumTot;

  const q = useQuery({
    queryKey: ["aanmeetdagen-list", params],
    queryFn: () => adminGet("/api/admin/aanmeetdagen", params),
  });

  const items = q.data?.items || q.data || [];

  const groepen = React.useMemo(() => {
    const map = new Map();
    for (const d of items) {
      const key = d.werkgever_naam || "Onbekende werkgever";
      if (!map.has(key)) map.set(key, { naam: key, werkgever_id: d.werkgever_id, items: [] });
      map.get(key).items.push(d);
    }
    return Array.from(map.values()).sort((a, b) => a.naam.localeCompare(b.naam, "nl"));
  }, [items]);

  return (
    <div data-testid="aanmeetdagen-page">
      <PageHeader
        title="Aanmeetdagen"
        subtitle="Gegroepeerd per werkgever — kalender-weergave komt in v1.1"
        actions={<ViewModeToggle value={view} onChange={setView} testid="aanmeetdagen-viewmode" />}
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <select
          value={werkgeverId}
          onChange={(e) => setWerkgeverId(e.target.value)}
          aria-label="Werkgever-filter"
          data-testid="aanmeetdagen-werkgever-filter"
          className="min-w-[14rem] rounded-md border border-slate-300 bg-white px-3 py-1.5 fulkra-tekst"
        >
          <option value="">Alle werkgevers</option>
          {(werkgeversQ.data?.items || []).map((w) => (
            <option key={w.werkgever_id} value={w.werkgever_id}>{w.bedrijfsnaam}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          Vanaf
          <input type="date" value={datumVan} onChange={(e) => setDatumVan(e.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 fulkra-tekst" />
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          Tot
          <input type="date" value={datumTot} onChange={(e) => setDatumTot(e.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 fulkra-tekst" />
        </label>
        {datumVan || datumTot ? (
          <button type="button" onClick={() => { setDatumVan(""); setDatumTot(""); }}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
            Datum wissen
          </button>
        ) : null}
      </div>

      <ErrorBanner error={q.error} />
      <OvergeslagenBanner
        aantal={q.data?.overgeslagen} enkelvoud="aanmeetdag" meervoud="aanmeetdagen"
        testid="admin-aanmeetdagen-overgeslagen"
      />

      {q.isLoading ? (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center fulkra-tekst text-slate-400">Laden…</div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center fulkra-tekst text-slate-400">
          Geen aanmeetdagen voor deze selectie.
        </div>
      ) : (
        <div className="space-y-6">
          {groepen.map((groep) => (
            <section key={groep.werkgever_id || groep.naam} className="rounded-md border border-slate-200 bg-white shadow-sm">
              <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
                <div>
                  <h3 className="fulkra-tekst font-semibold text-slate-900">{groep.naam}</h3>
                  <p className="text-xs text-slate-500">{groep.items.length} aanmeetdag{groep.items.length === 1 ? "" : "en"}</p>
                </div>
              </header>
              {view === "rijen" ? (
                <div className="overflow-x-auto" data-testid={`aanmeetdagen-tabel-${groep.werkgever_id || groep.naam}`}>
                  <table className="min-w-full divide-y divide-slate-200 fulkra-tekst">
                    <thead className="bg-white text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left">Datum</th>
                        <th className="px-4 py-2 text-left">Locatie</th>
                        <th className="px-4 py-2 text-left">Status</th>
                        <th className="px-4 py-2 text-left">Aanmeter</th>
                        <th className="px-4 py-2 text-left">Toegewezen</th>
                        <th className="px-4 py-2 text-right">Acties</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {groep.items.map((d) => (
                        <tr key={d.aanmeetdag_id} className="hover:bg-slate-50" data-testid={`aanmeetdag-row-${d.aanmeetdag_id}`}>
                          <td className="px-4 py-2 font-medium text-slate-900">{formatDateOnly(d.datum)}</td>
                          <td className="px-4 py-2 text-slate-600">{d.aanmeet_locatie || d.locatie || "—"}</td>
                          <td className="px-4 py-2"><StatusBadge value={d.status} testid={`aanmeetdag-status-${d.aanmeetdag_id}`} /></td>
                          <td className="px-4 py-2 text-xs text-slate-600">{d.aanmeet_team_id ? "Team toegewezen" : "—"}</td>
                          <td className="px-4 py-2 text-xs text-slate-500">{d.aanmeet_team_id ? "Ja" : "Nee"}</td>
                          <td className="px-4 py-2 text-right text-xs text-slate-400">—</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
                  {groep.items.map((d) => (
                    <article key={d.aanmeetdag_id} className="flex flex-col gap-2 rounded-lg border border-slate-200 p-4" data-testid={`aanmeetdag-tegel-${d.aanmeetdag_id}`}>
                      <div className="flex items-center justify-between">
                        <span className="fulkra-kop3 font-semibold text-slate-900">{formatDateOnly(d.datum)}</span>
                        <StatusBadge value={d.status} testid={`aanmeetdag-status-${d.aanmeetdag_id}`} />
                      </div>
                      <div className="text-xs text-slate-600">{d.aanmeet_locatie || d.locatie || "Locatie nog niet ingevuld"}</div>
                      {d.aanmeet_team_id ? (
                        <div className="text-xs text-slate-500">Team toegewezen</div>
                      ) : (
                        <div className="text-xs italic text-slate-400">Nog geen aanmeter gekoppeld</div>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      <p className="mt-4 text-xs text-slate-400">
        Tip: kalender-weergave + slot-bezetting komt in v1.1. CRUD-acties zoals nieuwe aanmeetdag aanmaken / aanmeter koppelen volgen daarna.
      </p>
    </div>
  );
}

export { default as EmailTemplatesPage } from "./EmailTemplatesPage";
export { default as AuditLogPage } from "./AuditLogPage";
export { default as InstellingenPage } from "./InstellingenPage";

export function EmailTemplatesPageLegacyStub() {
  return (
    <StubList
      title="Email-templates"
      subtitle="Read-only lijst (edit-modal + preview iframe komen v3B.1)"
      queryKey="email-templates-list"
      endpoint="/api/admin/email-templates"
      testid="email-templates-page"
      mapper={{
        cols: 4,
        head: <tr><th className="px-4 py-2 text-left">Type</th><th className="px-4 py-2 text-left">Onderwerp</th><th className="px-4 py-2 text-left">Actief</th><th className="px-4 py-2 text-left">Aangepast</th></tr>,
        row: (t) => (
          <tr key={t.template_id} data-testid={`template-row-${t.type}`}>
            <td className="px-4 py-2 font-mono text-xs">{t.type}</td>
            <td className="px-4 py-2">{t.onderwerp}</td>
            <td className="px-4 py-2 text-xs">{t.actief ? "Ja" : "Nee"}</td>
            <td className="px-4 py-2 text-xs text-slate-500">{formatDate(t.updated_at)}</td>
          </tr>
        ),
      }}
    />
  );
}

export function AuditLogPageLegacyStub() {
  return (
    <StubList
      title="Audit-log"
      subtitle="Read-only — filters/CSV-export komen v3B.1"
      queryKey="audit-log-list"
      endpoint="/api/admin/audit-log"
      testid="audit-log-page"
      mapper={{
        cols: 5,
        head: <tr><th className="px-4 py-2 text-left">Wanneer</th><th className="px-4 py-2 text-left">User</th><th className="px-4 py-2 text-left">Actie</th><th className="px-4 py-2 text-left">Entiteit</th><th className="px-4 py-2 text-left">IP</th></tr>,
        row: (a) => (
          <tr key={a.audit_id || a.id} data-testid={`audit-row-${a.audit_id || a.id}`}>
            <td className="px-4 py-2 text-xs text-slate-500">{formatDate(a.created_at)}</td>
            <td className="px-4 py-2 text-xs">{a.user_email || a.user_id || "—"}</td>
            <td className="px-4 py-2 font-mono text-xs">{a.actie}</td>
            <td className="px-4 py-2 text-xs">{a.entiteit_type} / {a.entiteit_id?.slice(0,8) || "—"}</td>
            <td className="px-4 py-2 text-xs">{a.ip_address || "—"}</td>
          </tr>
        ),
      }}
    />
  );
}

export function InstellingenPageLegacyStub() {
  const q = useQuery({ queryKey: ["mfa-status"], queryFn: () => adminGet("/api/auth/admin/mfa/status") });
  return (
    <div data-testid="instellingen-page">
      <PageHeader title="Instellingen" subtitle="Account en MFA-status" />
      <ErrorBanner error={q.error} />
      <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="fulkra-tekst font-semibold text-slate-700">Twee-staps-verificatie (TOTP)</h2>
        {q.isLoading ? <p className="fulkra-tekst text-slate-500">Laden…</p> : (
          q.data?.enrolled ? (
            <p className="mt-2 fulkra-tekst text-emerald-700" data-testid="mfa-enrolled">
              MFA actief — {q.data.factors?.length} factor(en) geregistreerd.
            </p>
          ) : (
            <p className="mt-2 fulkra-tekst text-amber-700" data-testid="mfa-not-enrolled">
              MFA is nog niet geactiveerd. Enrollment-UI volgt in v3B.1.
            </p>
          )
        )}
      </div>
    </div>
  );
}
